import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';

import { Box, Button, Divider, Popover, Stack, TextField, Typography } from '@mui/material';
import { buttonStyles, popoverPaperStyles } from '@pages/CustomPoiTypes.styles';
import { translatedCount, type LocalizedText } from '@store/api/poiTypeEndpoints';
import { usePermission } from '@store/hooks';
import { useState } from 'react';
import { GlobeIcon } from 'react-line-awesome';
import LocalesEditor from './LocalesEditor';

interface Props {
  label: string;
  value: LocalizedText;
  onChange: (value: LocalizedText) => void;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  autoFocus?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** A text of several lines, in the box and in every translation: a paragraph of a report. */
  multiline?: boolean;
  /** How long the text may get in one locale. */
  maxLength?: number;
  /**
   * For a text that may be left out altogether. The badge then warns only of a text that is
   * written in some languages and not in others, not of one that is simply not there.
   */
  optional?: boolean;
  /**
   * The language to put the cursor in when the translations open, for a caller showing the
   * content in that language. Without it, nothing in the popover takes focus.
   */
  focusLocale?: string;
  /**
   * For a text that is printed in whatever language it has when the reader's is missing: the
   * translations then also list every language the text already carries, enabled on this
   * server or not, with this line under those that are not. A report text needs it. A preset
   * comes with languages a server may not have enabled, and a report prints them to a reader
   * of that language, or to everyone once the enabled ones are emptied; without their boxes
   * the author can neither see nor remove what is printed.
   */
  carriedHint?: string;
}

/**
 * An input for one user-authored label across every enabled locale.
 *
 * The box itself edits the primary locale — the one the server requires, so the common
 * case stays a single field — and the badge opens the rest. The badge doubles as the
 * translation status, which is the only place the UI shows that a label is incomplete;
 * an untranslated locale is legal and falls back at render time.
 */
const LocalizedTextField: React.FC<Props> = ({
  label, value, onChange, placeholder, helperText, required, autoFocus, size = 'small', disabled, multiline,
  maxLength, optional, focusLocale, carriedHint,
}) => {
  const { t } = useLocalization();
  const { primary, enabled } = useCustomizationLocales();
  const { can } = usePermission();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // The languages are the server's, and here is where a missing one is noticed: whoever may
  // change them gets the drawer that does, opened in place. Going to the server configuration
  // page for it would unmount a drawer this field may stand in, with what was typed there.
  // The drawer is mounted only once it is asked for: a page holds many of these fields.
  const [localesEditor, setLocalesEditor] = useState<'unused' | 'open' | 'closed'>('unused');

  // A language stays listed once its box has been emptied, so it does not vanish from under
  // the cursor; the request leaves an empty one out, and it is gone after the save.
  const carried = carriedHint === undefined ? [] : Object.keys(value).filter((locale) => !enabled.includes(locale));
  const locales = [...enabled, ...carried];

  const filled = translatedCount(value, locales);
  const isComplete = filled === locales.length || (Boolean(optional) && filled === 0);

  // The same shape for the box and for each translation.
  const shape = { multiline, minRows: multiline ? 3 : undefined, slotProps: { htmlInput: { maxLength } } };

  const setLocale = (locale: string, text: string) => onChange({ ...value, [locale]: text });

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          size={size}
          label={label}
          required={required}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          helperText={helperText}
          value={value[primary] ?? ''}
          onChange={(event) => setLocale(primary, event.target.value)}
          {...shape}
        />
        {/* Only worth showing when there is more than one language to fill in. */}
        {locales.length > 1 && (
          <Button
            size="small"
            variant="outlined"
            color={isComplete ? 'inherit' : 'warning'}
            startIcon={<GlobeIcon />}
            disabled={disabled}
            onClick={(event) => setAnchor(event.currentTarget)}
            // Matched to the input's own height rather than nudged with a margin, so the two
            // stay level whatever the field size is. `flex-start` keeps them aligned when a
            // helper text makes the field taller than the button.
            sx={{ height: size === 'small' ? 40 : 56, flex: 'none', minWidth: 0 }}
            title={t('Customization_EditTranslations')}
          >
            {filled}/{locales.length}
          </Button>
        )}
      </Stack>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: popoverPaperStyles } }}
      >
        {/* Header, divider, body — the same three-part chrome the drawers use. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ color: 'text.secondary', display: 'inline-flex', fontSize: 15 }}><GlobeIcon /></Box>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
            {t('Customization_Translations')}
          </Typography>
          {can('server_config:put') && (
            <Button
              size="small"
              sx={buttonStyles}
              onClick={() => {
                setAnchor(null);
                setLocalesEditor('open');
              }}
            >
              {t('Customization_EditLocales')}
            </Button>
          )}
        </Stack>

        <Divider />

        <Stack spacing={1.5} sx={{ p: 2 }}>
          {locales.map((locale) => (
            <TextField
              key={locale}
              fullWidth
              size="small"
              label={locale === primary
                ? `${locale.toUpperCase()} · ${t('Customization_PrimaryLocale')}`
                : locale.toUpperCase()}
              value={value[locale] ?? ''}
              autoFocus={locale === focusLocale}
              onChange={(event) => setLocale(locale, event.target.value)}
              placeholder={locale === primary ? undefined : t('Customization_FallsBackToPrimary')}
              helperText={carried.includes(locale) ? carriedHint : undefined}
              {...shape}
            />
          ))}

          <Typography variant="caption" color="text.secondary">
            {t('Customization_EmptyLocalesFallBack')}
          </Typography>
        </Stack>
      </Popover>

      {localesEditor !== 'unused' && (
        <LocalesEditor open={localesEditor === 'open'} onClose={() => setLocalesEditor('closed')} />
      )}
    </Box>
  );
};

export default LocalizedTextField;
